import { useRef, useState } from 'react'
import { Field } from './Inputs'
import InfoTip from './InfoTip'
import type { PaintInputs, Room } from '../calculators/paint'
import type { Locale } from '../i18n/locale'
import { ROOM_PRESETS, CUSTOM_ROOM_KEY, toUnit, matchedPreset, dropdownValue, presetLabel } from '../lib/roomPresets'

export type PaintWizardGoal = 'forward' | 'reverse'

interface WizardCopy {
  doorsTotal: string
  windowsTotal: string
  coverageTip: string
  wasteTip: string
}

interface Props {
  locale: Locale
  copy: WizardCopy
  i: PaintInputs
  set: (patch: Partial<PaintInputs>) => void
  setRoom: (idx: number, patch: Partial<Room>) => void
  addRoom: () => void
  removeRoom: (idx: number) => void
  goal: PaintWizardGoal
  setGoal: (g: PaintWizardGoal) => void
  onClose: () => void
}

function tr(locale: Locale, en: string, fr: string): string {
  return locale === 'fr' ? fr : en
}

function ChipButton({
  selected, onClick, children,
}: { selected: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full rounded-lg border p-4 text-left transition-colors ${
        selected
          ? 'border-brand bg-brand-tint/60 text-text'
          : 'border-border bg-surface text-text-body hover:border-brand hover:bg-surface-2'
      }`}
    >
      {children}
    </button>
  )
}

function StepDots({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex items-center gap-1.5">
      {Array.from({ length: total }, (_, idx) => idx + 1).map((n) => (
        <span
          key={n}
          className={`h-1.5 w-1.5 rounded-full ${
            n === current ? 'bg-brand' : n < current ? 'bg-brand/40' : 'bg-border'
          }`}
        />
      ))}
    </div>
  )
}

export default function PaintWizard({
  locale, copy, i, set, setRoom, addRoom, removeRoom, goal, setGoal, onClose,
}: Props) {
  const [step, setStep] = useState(1)
  // Forward: Goal, Unit, Area+surfaces, Coats, Openings, Primer+output (6 steps).
  // Reverse: Goal, Unit+output, Paint amount, Coats, Coverage (5 steps).
  const totalSteps = goal === 'forward' ? 6 : 5
  const T = (en: string, fr: string) => tr(locale, en, fr)
  const metric = i.unit === 'metric'

  // Room-preset wiring — mirrors the main PaintCalculator so the quiz's
  // dropdown behaviour matches: pick a preset → dimensions auto-fill, name
  // hidden; pick "Custom room" → name input shows; "Select a room" is the
  // placeholder for empty-named rooms (initial state or after Add Room).
  const cvDim = (ft: number) => toUnit(ft, i.unit)
  const roomDefault = (idx: number) =>
    locale === 'fr' ? `Pièce ${idx + 1}` : `Room ${idx + 1}`
  const applyPreset = (idx: number, label: string) => {
    if (label === CUSTOM_ROOM_KEY) {
      const current = i.rooms[idx].name
      if (!current || matchedPreset(current)) setRoom(idx, { name: roomDefault(idx) })
      return
    }
    const p = ROOM_PRESETS.find((rp) => rp.label === label)
    if (!p) return
    setRoom(idx, { name: presetLabel(p, locale), length: cvDim(p.lFt), width: cvDim(p.wFt), height: cvDim(p.hFt) })
  }

  const advanceTimer = useRef<number | null>(null)
  const cancelAdvance = () => {
    if (advanceTimer.current) {
      window.clearTimeout(advanceTimer.current)
      advanceTimer.current = null
    }
  }
  const next = () => {
    cancelAdvance()
    if (step >= totalSteps) {
      onClose()
    } else {
      setStep((s) => s + 1)
    }
  }
  const back = () => {
    cancelAdvance()
    setStep((s) => Math.max(1, s - 1))
  }
  const pickAndAdvance = (apply: () => void) => {
    apply()
    cancelAdvance()
    advanceTimer.current = window.setTimeout(() => {
      advanceTimer.current = null
      setStep((s) => (s >= totalSteps ? s : s + 1))
      if (step >= totalSteps) onClose()
    }, 250)
  }

  // Step 1 — Goal: forward (need quantity) vs reverse (have leftover).
  function StepGoal() {
    return (
      <div className="space-y-3">
        <h2 className="text-2xl font-semibold text-text">
          {T('What are you trying to figure out?', 'Que voulez-vous calculer?')}
        </h2>
        <p className="text-sm text-text-muted">
          {T('Both work the same way — just pick the one you need.', 'Les deux fonctionnent de la même façon — choisissez le bon.')}
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <ChipButton selected={goal === 'forward'} onClick={() => pickAndAdvance(() => setGoal('forward'))}>
            <div className="text-lg font-semibold">🛒 {T('How much paint do I need?', 'Combien de peinture me faut-il?')}</div>
            <div className="mt-1 text-xs text-text-muted">{T('I know the rooms — tell me the gallons / litres', 'Je connais les pièces — dites-moi les gallons / litres')}</div>
          </ChipButton>
          <ChipButton selected={goal === 'reverse'} onClick={() => pickAndAdvance(() => setGoal('reverse'))}>
            <div className="text-lg font-semibold">📏 {T('How much can I paint with what I have?', 'Quelle surface puis-je peindre avec ce que j\'ai?')}</div>
            <div className="mt-1 text-xs text-text-muted">{T('I have leftover paint — what will it cover?', 'J\'ai de la peinture en trop — quelle surface?')}</div>
          </ChipButton>
        </div>
      </div>
    )
  }

  // Step 2 — Unit system (forward) OR unit + output unit (reverse).
  // Reverse needs both because we need to interpret "I have N gallons or litres".
  function StepUnit() {
    return (
      <div className="space-y-3">
        <h2 className="text-2xl font-semibold text-text">
          {T('What units do you measure in?', 'Dans quelles unités mesurez-vous?')}
        </h2>
        <p className="text-sm text-text-muted">
          {T('Pick whichever feels natural — you can change this later.', 'Choisissez ce qui vous convient — modifiable plus tard.')}
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <ChipButton
            selected={i.unit === 'imperial'}
            onClick={() => pickAndAdvance(() => set({ unit: 'imperial', outputUnit: 'gallon' }))}
          >
            <div className="text-lg font-semibold">{T('Feet & gallons', 'Pieds et gallons')}</div>
            <div className="mt-1 text-xs text-text-muted">{T('Imperial — common in the US and parts of Canada', 'Impérial — courant aux É.-U. et dans certaines régions canadiennes')}</div>
          </ChipButton>
          <ChipButton
            selected={i.unit === 'metric'}
            onClick={() => pickAndAdvance(() => set({ unit: 'metric', outputUnit: 'litre' }))}
          >
            <div className="text-lg font-semibold">{T('Metres & litres', 'Mètres et litres')}</div>
            <div className="mt-1 text-xs text-text-muted">{T('Metric — used on most Canadian paint cans', 'Métrique — utilisé sur la plupart des cannettes canadiennes')}</div>
          </ChipButton>
        </div>
      </div>
    )
  }

  // Forward Step 3 — Area + surface decision (rolled into one step to save clicks).
  function StepArea() {
    const lin = metric ? 'm' : (locale === 'fr' ? 'pi' : 'ft')
    const sqLabel = metric ? 'm²' : (locale === 'fr' ? 'pi²' : 'sq ft')
    return (
      <div className="space-y-3">
        <h2 className="text-2xl font-semibold text-text">
          {T('Tell me about the space', 'Décrivez l\'espace')}
        </h2>

        {/* Sub-toggle: dimensions vs total area */}
        <div className="grid gap-3 sm:grid-cols-2">
          <ChipButton
            selected={i.inputMode === 'dimensions'}
            onClick={() => set({ inputMode: 'dimensions' })}
          >
            <div className="text-base font-semibold">{T('Enter room dimensions', 'Saisir les dimensions des pièces')}</div>
            <div className="mt-1 text-xs text-text-muted">{T('I\'ll type L × W × H per room', 'Je tape L × l × h par pièce')}</div>
          </ChipButton>
          <ChipButton
            selected={i.inputMode === 'area'}
            onClick={() => set({ inputMode: 'area' })}
          >
            <div className="text-base font-semibold">{T('I know the total paintable area', 'Je connais la surface peinturable totale')}</div>
            <div className="mt-1 text-xs text-text-muted">{T('Just one number', 'Un seul nombre')}</div>
          </ChipButton>
        </div>

        {/* Inputs based on mode */}
        <div className="mt-4 rounded-lg border border-border bg-surface p-4">
          {i.inputMode === 'dimensions' ? (
            <div className="space-y-3">
              {i.rooms.map((room, idx) => {
                const isCustom = dropdownValue(room.name) === CUSTOM_ROOM_KEY
                return (
                <div key={idx} className="rounded-md border border-border p-3">
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <select
                      value={dropdownValue(room.name)}
                      onChange={(e) => applyPreset(idx, e.target.value)}
                      aria-label={T('Room type', 'Type de pièce')}
                      className={`rounded-md border border-border bg-surface-2 px-2 py-1.5 text-sm text-text-body focus:border-brand focus:outline-none ${isCustom ? '' : 'w-full sm:w-1/2'}`}
                    >
                      <option value="" disabled>{T('Select a room', 'Choisissez une pièce')}</option>
                      {ROOM_PRESETS.map((p) => (
                        <option key={p.label} value={p.label}>{presetLabel(p, locale)}</option>
                      ))}
                      <option value={CUSTOM_ROOM_KEY}>{T('Custom room', 'Pièce personnalisée')}</option>
                    </select>
                    {isCustom && (
                      <input
                        type="text"
                        value={room.name ?? ''}
                        onChange={(e) => setRoom(idx, { name: e.target.value })}
                        placeholder={T('Room name', 'Nom de la pièce')}
                        className="min-w-0 flex-1 rounded-md border border-border bg-surface px-2 py-1 text-sm text-text outline-none focus:border-brand"
                      />
                    )}
                    {i.rooms.length > 1 && (
                      <button type="button" onClick={() => removeRoom(idx)} className="text-xs text-danger hover:underline whitespace-nowrap">{T('Remove', 'Retirer')}</button>
                    )}
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <Field label={T('Length', 'Longueur')} value={room.length} onChange={(n) => setRoom(idx, { length: n })} suffix={lin} />
                    <Field label={T('Width', 'Largeur')} value={room.width} onChange={(n) => setRoom(idx, { width: n })} suffix={lin} />
                    <Field label={T('Height', 'Hauteur')} value={room.height} onChange={(n) => setRoom(idx, { height: n })} suffix={lin} />
                  </div>
                </div>
                )
              })}
              <button type="button" onClick={addRoom} className="w-full rounded-md border border-dashed border-border py-2 text-sm font-medium text-brand-text hover:bg-surface-2">
                {T('+ Add another room', '+ Ajouter une pièce')}
              </button>
            </div>
          ) : (
            <Field
              label={T('Total paintable area', 'Surface peinturable totale')}
              value={i.totalArea}
              onChange={(n) => set({ totalArea: n })}
              suffix={sqLabel}
            />
          )}
        </div>

        {/* Ceiling toggle - prominent so it doesn't get missed */}
        <label className="mt-3 flex cursor-pointer items-start gap-3 rounded-lg border border-border bg-surface p-4 hover:bg-surface-2">
          <input
            type="checkbox"
            checked={i.includeCeiling}
            onChange={(e) => set({ includeCeiling: e.target.checked })}
            className="mt-1"
          />
          <span>
            <span className="block text-base font-semibold">{T('Also paint the ceilings', 'Peindre aussi les plafonds')}</span>
            <span className="mt-1 block text-xs text-text-muted">
              {T('Adds the ceiling area to the paint total. Skip if you\'re only doing walls.', 'Ajoute la surface du plafond au total. À éviter si vous ne peignez que les murs.')}
            </span>
          </span>
        </label>
      </div>
    )
  }

  // Forward Step 4 — How many coats (preset chips). Reverse uses this same step.
  function StepCoats() {
    const presets = [
      { n: 1, label: T('1 coat', '1 couche'), desc: T('Touch-up only — same colour over a clean surface', 'Retouche seulement — même couleur sur surface propre') },
      { n: 2, label: T('2 coats · Recommended', '2 couches · Recommandé'), desc: T('What most paint actually needs, despite the can claims', 'Ce que la plupart des peintures demandent vraiment, malgré les promesses') },
      { n: 3, label: T('3 coats', '3 couches'), desc: T('Heavy colour change or covering stains', 'Changement de couleur important ou couvrir des taches') },
    ]
    return (
      <div className="space-y-3">
        <h2 className="text-2xl font-semibold text-text">
          {T('How many coats?', 'Combien de couches?')}
        </h2>
        <p className="text-sm text-text-muted">
          {T('2 coats is essentially always the right answer for walls — "one-coat" paint claims rarely hold up in real life.', '2 couches est presque toujours la bonne réponse pour les murs — les promesses « une couche » tiennent rarement la route.')}
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          {presets.map((p) => (
            <ChipButton key={p.n} selected={i.coats === p.n} onClick={() => pickAndAdvance(() => set({ coats: p.n }))}>
              <div className="text-base font-semibold">{p.label}</div>
              <div className="mt-1 text-xs text-text-muted">{p.desc}</div>
            </ChipButton>
          ))}
        </div>
      </div>
    )
  }

  // Forward Step 5 — Doors + windows (openings).
  function StepOpenings() {
    return (
      <div className="space-y-3">
        <h2 className="text-2xl font-semibold text-text">
          {T('Any doors or windows to skip?', 'Des portes ou fenêtres à exclure?')}
        </h2>
        <p className="text-sm text-text-muted">
          {T('Each door subtracts ~21 sq ft, each window ~15 sq ft. Doesn\'t need to be exact — close is good.', 'Chaque porte soustrait environ 21 pi², chaque fenêtre environ 15 pi². Pas besoin d\'être précis — une estimation suffit.')}
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <div className="rounded-lg border border-border bg-surface p-4">
            <Field label={copy.doorsTotal} value={i.doors} onChange={(n) => set({ doors: Math.max(0, Math.round(n)) })} />
          </div>
          <div className="rounded-lg border border-border bg-surface p-4">
            <Field label={copy.windowsTotal} value={i.windows} onChange={(n) => set({ windows: Math.max(0, Math.round(n)) })} />
          </div>
        </div>
      </div>
    )
  }

  // Forward Step 6 — Primer + extras (coverage rate, waste).
  function StepExtras() {
    return (
      <div className="space-y-4">
        <h2 className="text-2xl font-semibold text-text">{T('Almost done — finishing touches', 'Presque terminé — derniers ajustements')}</h2>
        <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border bg-surface p-4 hover:bg-surface-2">
          <input
            type="checkbox"
            checked={i.needPrimer}
            onChange={(e) => set({ needPrimer: e.target.checked })}
            className="mt-1"
          />
          <span>
            <span className="block text-base font-semibold">{T('Include primer in the count', 'Inclure l\'apprêt dans le total')}</span>
            <span className="mt-1 block text-xs text-text-muted">
              {T('Recommended for bare drywall, patched walls, or going light-over-dark. Skip for like-for-like touch-ups.', 'Recommandé pour le gypse nu, les murs réparés ou pour passer du clair sur du foncé. À éviter pour les retouches semblables.')}
            </span>
          </span>
        </label>

        <div className="rounded-lg border border-border bg-surface p-4">
          <span className="mb-1 flex items-center text-sm font-medium text-text-body">
            {T('Coverage per gallon', 'Couverture par gallon')}
            <InfoTip text={copy.coverageTip} />
          </span>
          <Field
            label=""
            value={i.coveragePerGallon}
            onChange={(n) => set({ coveragePerGallon: Math.max(50, n) })}
            min={50}
            step={10}
            suffix={metric ? 'sq ft/gal' : 'sq ft/gal'}
          />
          <p className="mt-2 text-xs text-text-subtle">
            {T('350 is a good default for most latex paint. Specialty / oil-based may be lower.', '350 est une bonne valeur par défaut pour la peinture au latex. Les peintures spéciales ou à l\'huile peuvent être plus basses.')}
          </p>
        </div>

        <div className="rounded-lg border border-border bg-surface p-4">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium text-text-body">
              {T('Extra allowance', 'Marge supplémentaire')}
              <InfoTip text={copy.wasteTip} />
            </span>
            <span className="tnum text-brand-text">{i.wastePct}%</span>
          </div>
          <input
            type="range" min={0} max={25} step={1} value={i.wastePct}
            onChange={(e) => set({ wastePct: parseInt(e.target.value) })}
            className="mt-2 w-full accent-brand"
          />
        </div>
      </div>
    )
  }

  // Reverse Step 3 — How much paint do you have.
  function StepPaintAmount() {
    const unitLabel = i.outputUnit === 'gallon' ? T('gallons', 'gallons') : T('litres', 'litres')
    return (
      <div className="space-y-3">
        <h2 className="text-2xl font-semibold text-text">
          {T('How much paint do you have?', 'Combien de peinture avez-vous?')}
        </h2>
        <p className="text-sm text-text-muted">
          {T(`Enter the amount in ${unitLabel}. We'll figure out how far it goes.`, `Entrez la quantité en ${unitLabel}. Nous calculerons jusqu'où elle ira.`)}
        </p>
        <div className="rounded-lg border border-border bg-surface p-4">
          <Field
            label={T(`Amount in ${unitLabel}`, `Quantité en ${unitLabel}`)}
            value={i.gallonsHave}
            onChange={(n) => set({ gallonsHave: Math.max(0, n) })}
            min={0}
            step={0.25}
            suffix={i.outputUnit === 'gallon' ? 'gal' : 'L'}
          />
        </div>
        <p className="text-xs text-text-subtle">
          {T(`Currently set to ${i.outputUnit === 'gallon' ? 'gallons' : 'litres'} based on your unit choice — go back to change.`, `Réglé actuellement sur ${i.outputUnit === 'gallon' ? 'gallons' : 'litres'} selon votre choix d\'unité — revenez pour modifier.`)}
        </p>
      </div>
    )
  }

  // Reverse Step 5 — Coverage rate + confirm. Same coverage tweak as forward extras.
  function StepReverseCoverage() {
    return (
      <div className="space-y-3">
        <h2 className="text-2xl font-semibold text-text">{T('One more — paint coverage', 'Encore un — couverture de la peinture')}</h2>
        <p className="text-sm text-text-muted">
          {T('Your paint can\'s label says something like "covers 350 sq ft per gallon" — tweak if different.', 'L\'étiquette de votre peinture indique quelque chose comme « couvre 350 pi² par gallon » — ajustez au besoin.')}
        </p>
        <div className="rounded-lg border border-border bg-surface p-4">
          <Field
            label={T('Coverage per gallon (sq ft)', 'Couverture par gallon (pi²)')}
            value={i.coveragePerGallon}
            onChange={(n) => set({ coveragePerGallon: Math.max(50, n) })}
            min={50}
            step={10}
          />
        </div>
        <div className="rounded-lg border border-brand/40 bg-brand-tint/40 p-4 text-sm">
          <p className="font-medium text-text">
            {T(
              `You said: ${i.gallonsHave} ${i.outputUnit === 'gallon' ? 'gal' : 'L'} of paint, ${i.coats} coat${i.coats === 1 ? '' : 's'}, ${i.coveragePerGallon} sq ft/gal coverage.`,
              `Vous avez indiqué : ${i.gallonsHave} ${i.outputUnit === 'gallon' ? 'gal' : 'L'} de peinture, ${i.coats} couche${i.coats === 1 ? '' : 's'}, couverture de ${i.coveragePerGallon} pi²/gal.`,
            )}
          </p>
          <p className="mt-2 text-text-muted">
            {T('Tap “See my result” below for how much you can paint — or Back to adjust.', 'Touchez « Voir mon résultat » ci-dessous pour la surface à peindre — ou Précédent pour ajuster.')}
          </p>
        </div>
      </div>
    )
  }

  const renderStep = () => {
    if (step === 1) return <StepGoal />
    if (step === 2) return <StepUnit />
    if (goal === 'forward') {
      if (step === 3) return <StepArea />
      if (step === 4) return <StepCoats />
      if (step === 5) return <StepOpenings />
      if (step === 6) return <StepExtras />
    } else {
      if (step === 3) return <StepPaintAmount />
      if (step === 4) return <StepCoats />
      if (step === 5) return <StepReverseCoverage />
    }
    return null
  }

  const hideNext = step === 1 || step === 2

  return (
    <div className="rounded-xl border border-border bg-surface p-5 lg:col-span-3">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 text-xs text-text-muted">
          <span className="font-medium uppercase tracking-wide">
            {T('Guided quiz', 'Quiz guidé')} · {step}/{totalSteps}
          </span>
          <StepDots current={step} total={totalSteps} />
        </div>
        <button type="button" onClick={onClose} className="text-xs text-text-muted hover:underline">
          {T('Skip quiz', 'Quitter le quiz')}
        </button>
      </div>

      <div className="my-6">{renderStep()}</div>

      <div className="flex items-center justify-between border-t border-border pt-4">
        <button
          type="button"
          onClick={back}
          disabled={step === 1}
          className="rounded-md border border-border px-4 py-2 text-sm font-medium text-text-body hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {T('← Back', '← Précédent')}
        </button>
        {hideNext ? (
          <span className="text-xs italic text-text-subtle">
            {T('Pick to continue →', 'Choisissez pour continuer →')}
          </span>
        ) : (
          <button
            type="button"
            onClick={next}
            className="rounded-md bg-primary px-5 py-2 text-sm font-semibold text-on-primary hover:opacity-90"
          >
            {step >= totalSteps ? T('See my result →', 'Voir mon résultat →') : T('Next →', 'Suivant →')}
          </button>
        )}
      </div>
    </div>
  )
}
